import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { apiUrl } from '../services/api';

// Owner-only admin dashboard: cost by service/api, costliest users (warn/ban),
// spend kill-switch controls, and beta usage analytics. Charts are hand-rolled
// SVG/CSS to avoid adding a charting dependency.

async function getJson(path) {
  const res = await fetch(apiUrl(path), { credentials: 'include' });
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
}
async function postJson(path, body) {
  const res = await fetch(apiUrl(path), {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
  });
  return res.json().catch(() => ({}));
}

const fmtUsd = (n) => `$${Number(n || 0).toFixed(Number(n) < 1 ? 4 : 2)}`;

function BarRow({ label, sub, value, max, fmt }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0' }}>
      <div style={{ width: 180, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}{sub ? <span style={{ color: 'var(--tp-muted)', marginLeft: 6, fontSize: 11 }}>{sub}</span> : null}
      </div>
      <div style={{ flex: 1, background: 'var(--tp-surface-2)', borderRadius: 5, height: 16 }}>
        <div style={{ width: `${pct}%`, background: '#c49b63', height: '100%', borderRadius: 5 }} />
      </div>
      <div style={{ width: 90, textAlign: 'right', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
        {fmt ? fmt(value) : value}
      </div>
    </div>
  );
}

function LineChart({ points, height = 90 }) {
  if (!points || points.length === 0) return <div style={{ color: 'var(--tp-muted)', fontSize: 13 }}>No data yet.</div>;
  const w = 600, h = height, pad = 4;
  const max = Math.max(...points.map((p) => p.y), 0.000001);
  const step = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const coords = points.map((p, i) => [pad + i * step, h - pad - (p.y / max) * (h - pad * 2)]);
  const d = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      <path d={d} fill="none" stroke="#c49b63" strokeWidth="2" />
    </svg>
  );
}

function Stat({ label, value }) {
  return (
    <div className="tp-card" style={{ padding: '10px 14px', minWidth: 96, textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 800 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--tp-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>
  );
}

export default function Admin() {
  const { loading, isOwner } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [costly, setCostly] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [gran, setGran] = useState('month');
  const [limitInput, setLimitInput] = useState('');
  const [stateInput, setStateInput] = useState('auto');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!loading && !isOwner) navigate('/');
  }, [loading, isOwner, navigate]);

  const load = useCallback(async () => {
    try {
      setErr('');
      const [o, c, a] = await Promise.all([
        getJson('/api/admin/overview'),
        getJson('/api/admin/users/costly'),
        getJson(`/api/admin/analytics?granularity=${gran}`),
      ]);
      setOverview(o);
      setCostly(c.users || []);
      setAnalytics(a);
      setLimitInput(String(o.status.limit_usd));
      setStateInput(o.status.state);
    } catch (_e) {
      setErr('Failed to load admin data.');
    }
  }, [gran]);

  useEffect(() => { if (isOwner) load(); }, [isOwner, load]);

  const saveSettings = async () => {
    await postJson('/api/admin/settings', { monthly_cost_limit_usd: limitInput, killswitch_state: stateInput });
    load();
  };
  const ban = async (u) => {
    const action = u.status === 'banned' ? 'unban' : 'ban';
    await postJson(`/api/admin/users/${u.user_id}/${action}`, {});
    load();
  };

  if (loading || !isOwner) return null;

  const status = overview?.status;
  const svcMax = Math.max(...(overview?.by_service || []).map((s) => s.cost_usd), 0);
  const apiMax = Math.max(...(overview?.by_api || []).map((s) => s.cost_usd), 0);
  const costMax = Math.max(...costly.map((u) => u.cost_usd), 0);
  const sumMax = Math.max(...((analytics?.series) || []).map((b) => b.unique), 0);
  const overBudget = status && status.paused;

  return (
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      <h1 className="tp-section__title" style={{ marginTop: 0 }}>Admin · Cost &amp; Usage</h1>
      {err && <div style={{ color: '#e15759', marginBottom: 12 }}>{err}</div>}

      {/* Spend + kill-switch */}
      <div className="tp-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--tp-muted)' }}>Estimated spend this month</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: overBudget ? '#e15759' : 'inherit' }}>
              {fmtUsd(status?.month_usd)} <span style={{ fontSize: 15, color: 'var(--tp-muted)', fontWeight: 500 }}>/ {fmtUsd(status?.limit_usd)}</span>
            </div>
          </div>
          <div style={{ fontSize: 13, color: overBudget ? '#e15759' : 'var(--tp-muted)' }}>
            {overBudget ? `PAUSED (non-owner) · resumes ${status?.resumes}` : `Active · state: ${status?.state}`}
          </div>
        </div>
        <div style={{ marginTop: 10, background: 'var(--tp-surface-2)', borderRadius: 6, height: 12 }}>
          <div style={{
            width: `${status && status.limit_usd > 0 ? Math.min(100, (status.month_usd / status.limit_usd) * 100) : 0}%`,
            height: '100%', borderRadius: 6, background: overBudget ? '#e15759' : '#c49b63',
          }} />
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 14, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12 }}>Monthly limit ($)
            <input type="number" min="0" step="1" value={limitInput} onChange={(e) => setLimitInput(e.target.value)}
              style={{ display: 'block', marginTop: 4, padding: '6px 8px', width: 120 }} />
          </label>
          <label style={{ fontSize: 12 }}>Kill-switch
            <select value={stateInput} onChange={(e) => setStateInput(e.target.value)} style={{ display: 'block', marginTop: 4, padding: '6px 8px' }}>
              <option value="auto">auto (trip at limit)</option>
              <option value="on">on (force pause)</option>
              <option value="off">off (never pause)</option>
            </select>
          </label>
          <button type="button" className="tp-btn" onClick={saveSettings}
            style={{ padding: '7px 14px', background: '#c49b63', color: '#1a1a1a', fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            Save
          </button>
        </div>
      </div>

      {/* Cost by service + api */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="tp-card">
          <h3 style={{ marginTop: 0 }}>Cost by service (this month)</h3>
          {(overview?.by_service || []).length === 0 && <div style={{ color: 'var(--tp-muted)', fontSize: 13 }}>No usage yet.</div>}
          {(overview?.by_service || []).map((s) => (
            <BarRow key={s.service} label={s.service} sub={`${s.units} calls`} value={s.cost_usd} max={svcMax} fmt={fmtUsd} />
          ))}
        </div>
        <div className="tp-card">
          <h3 style={{ marginTop: 0 }}>Cost by API (this month)</h3>
          {(overview?.by_api || []).length === 0 && <div style={{ color: 'var(--tp-muted)', fontSize: 13 }}>No usage yet.</div>}
          {(overview?.by_api || []).map((s) => (
            <BarRow key={`${s.service}/${s.api}`} label={`${s.service} · ${s.api}`} sub={`${s.units}`} value={s.cost_usd} max={apiMax} fmt={fmtUsd} />
          ))}
        </div>
      </div>

      {/* Daily trend */}
      <div className="tp-card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Daily cost trend (this month)</h3>
        <LineChart points={(overview?.daily || []).map((d) => ({ x: d.day, y: d.cost_usd }))} />
      </div>

      {/* Costliest users */}
      <div className="tp-card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Costliest users (this month)</h3>
        {costly.length === 0 && <div style={{ color: 'var(--tp-muted)', fontSize: 13 }}>No attributed usage yet.</div>}
        {costly.map((u) => (
          <div key={u.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid var(--tp-border)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {u.is_guest ? 'Guest' : (u.email || u.display_name || u.user_id)}
                {u.status === 'banned' && <span style={{ color: '#e15759', marginLeft: 6, fontSize: 11 }}>banned</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--tp-muted)' }}>{u.units} calls</div>
            </div>
            <div style={{ width: 90, textAlign: 'right', fontWeight: 700 }}>{fmtUsd(u.cost_usd)}</div>
            <button type="button" onClick={() => ban(u)}
              style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid var(--tp-border)', cursor: 'pointer', background: u.status === 'banned' ? 'transparent' : 'rgba(225,87,89,0.12)', color: u.status === 'banned' ? 'inherit' : '#e15759' }}>
              {u.status === 'banned' ? 'Unban' : 'Ban'}
            </button>
          </div>
        ))}
      </div>

      {/* Analytics */}
      <div className="tp-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>Usage analytics</h3>
          <select value={gran} onChange={(e) => setGran(e.target.value)} style={{ padding: '5px 8px' }}>
            <option value="day">by day</option>
            <option value="week">by week</option>
            <option value="month">by month</option>
            <option value="year">by year</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
          <Stat label="All time" value={analytics?.summary?.all_time ?? '–'} />
          <Stat label="This year" value={analytics?.summary?.this_year ?? '–'} />
          <Stat label="This month" value={analytics?.summary?.this_month ?? '–'} />
          <Stat label="This week" value={analytics?.summary?.this_week ?? '–'} />
          <Stat label="Today" value={analytics?.summary?.today ?? '–'} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginBottom: 8 }}>
          Unique users per {gran} — bar = unique, number = returning / new.
          &nbsp;All-time: {analytics?.summary?.accounts_all_time ?? 0} accounts · {analytics?.summary?.guests_all_time ?? 0} guests.
        </div>
        {(analytics?.series || []).slice(-24).map((b) => (
          <BarRow key={b.bucket} label={b.bucket} value={b.unique} max={sumMax}
            fmt={() => `${b.unique} (${b.returning} ret / ${b.new} new)`} />
        ))}
        {(analytics?.series || []).length === 0 && <div style={{ color: 'var(--tp-muted)', fontSize: 13 }}>No activity recorded yet.</div>}
      </div>
    </div>
  );
}
